import React, { useState } from 'react';
import { TwoStepVerificationError } from '../../../../common/request/types/twoStepVerification';
import {
  mapTwoStepVerificationErrorToChallengeErrorCode,
  mapTwoStepVerificationErrorToResource
} from '../constants/resources';
import useTwoStepVerificationContext from '../hooks/useTwoStepVerificationContext';
import { TwoStepVerificationActionType } from '../store/action';

type Props = {
  disabled: boolean;
  setCodeError: React.Dispatch<React.SetStateAction<string | null>>;
  // eslint-disable-next-line react/require-default-props
  className?: string;
};

/**
 * A button to trigger re-sending a 2SV email code.
 */
const ResendCodeButton: React.FC<Props> = ({ disabled, setCodeError, className }: Props) => {
  const {
    state: {
      userId,
      challengeId,
      actionType,
      renderInline,
      resources,
      eventService,
      requestService
    },
    dispatch
  } = useTwoStepVerificationContext();

  const [requestInFlight, setRequestInFlight] = useState<boolean>(false);

  const resendCode = async () => {
    setRequestInFlight(true);

    eventService.sendEmailResendRequestedEvent();

    const result = await requestService.twoStepVerification.sendEmailCode(userId, {
      challengeId,
      actionType
    });
    setRequestInFlight(false);
    if (result.isError) {
      if (
        result.error === TwoStepVerificationError.INVALID_USER_ID ||
        result.error === TwoStepVerificationError.INVALID_CHALLENGE_ID
      ) {
        dispatch({
          type: TwoStepVerificationActionType.SET_CHALLENGE_INVALIDATED,
          errorCode: mapTwoStepVerificationErrorToChallengeErrorCode(result.error)
        });
      } else {
        setCodeError(mapTwoStepVerificationErrorToResource(resources, result.error));
      }
    }
  };

  if (requestInFlight) {
    return (
      <p className={className}>
        <span className='spinner spinner-xs spinner-no-margin' />
      </p>
    );
  }

  const buttonLinkClassName = renderInline
    ? 'inline-challenge-body-button-link'
    : 'modal-body-button-link';

  return (
    <p className={className}>
      <button
        type='button'
        className={`${buttonLinkClassName} small`}
        onClick={resendCode}
        disabled={disabled || requestInFlight}>
        {resources.Action.Resend}
      </button>
    </p>
  );
};

export default ResendCodeButton;
